import { CommentModel } from '@/api/common';
import { BoardUserActivityType } from '@/api/workspaceSettings/queries';
import { CardType, getCardType, getCardTypeName, StorymapCard } from '@/store/main/StorymapCardDetails/types';
import { StorymapCollaborator } from '@/store/main/types';
import { Command, CommandResult, ICommandResult } from '@/_core/cqrs/types/commands';
import { uuid } from '@/_core/utils/uuid';
import * as storymapStoreModule from '@/pages/main/storymap/store';
import { Mention as EditorMention } from '@/pages/components/richTextEditor/utils';

export class RemoveCardComment extends Command<ICommandResult> {
    public BoardActivity: any;
    public StorymapId: string;
    public StoryMapSlug: string;
    public WorkspaceId: number;

    private constructor(public CardId: string, public CommentId: string) {
        super('RemoveCardComment');
    }

    public static async create(cardId: string, commentId: string, storymapId: string, storymapSlug: string, workspaceId: number) {
        const cmd = new RemoveCardComment(cardId, commentId);

        cmd.StorymapId = storymapId;
        cmd.StoryMapSlug = storymapSlug;
        cmd.WorkspaceId = workspaceId;

        const activityId = await uuid.create();

        const activity = {
            CardDescription: '',
            CardId: cardId,
            Datetime: new Date().toJSON(),
            EstimationUnit: '',
            Id: activityId,
            StorymapId: storymapId,
            WorkspaceId: workspaceId,
            Type: BoardUserActivityType.CardCommentRemoved,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;
        return cmd;
    }
}

export class EditCardComment extends Command<ICommandResult> {
    public BoardActivity: any;
    public StorymapId: string;
    public StoryMapSlug: string;
    public WorkspaceId: number;

    private constructor(
        public CardId: string,
        public CommentId: string,
        public CommentText: string,
        public NewlyMentionedIds: string[],
        public OriginallyMentionedIds: string[],
        public IsEverybodyMentioned: boolean,
        public CreatorUserName: string,
        public CreatorUserId: string
    ) {
        super('EditCardComment');
    }
    public static async create(
        cardId: string,
        cardName: string,
        comment: CommentModel,
        newlyMentionedUserIds: string[],
        originallyMentionedUserIds: string[],
        storymapId: string,
        storymapSlug: string,
        workspaceId: number
    ) {
        const cmd = new EditCardComment(
            cardId,
            comment.id,
            comment.commentText,
            newlyMentionedUserIds,
            originallyMentionedUserIds,
            newlyMentionedUserIds.includes('EVERYBODY'),
            initAppData.currentUser.currentUserFullName,
            initAppData.currentUser.currentUserId
        );

        cmd.StorymapId = storymapId;
        cmd.StoryMapSlug = storymapSlug;
        cmd.WorkspaceId = workspaceId;

        const activityId = await uuid.create();

        const activity = {
            CardDescription: '',
            CardId: cardId,
            CardName: cardName,
            Content: comment.commentText,
            Datetime: new Date().toJSON(),
            EstimationUnit: '',
            Id: activityId,
            StorymapId: storymapId,
            WorkspaceId: workspaceId,
            Type: BoardUserActivityType.CardCommentEdited,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;
        return cmd;
    }
}

export class ChangeCardStatus extends Command<ICommandResult> {
    public BoardActivity: any;

    private constructor(public Id: string, public StorymapId: string, public NewStatus: number, public TrackingOrder: number) {
        super('ChangeCardStatus');
    }

    public static async create(
        cardId: string,
        cardName: string,
        oldStatusName: string,
        newStatusName: string,
        storymapId: string,
        newStatus: number,
        trackingOrder: number
    ) {
        const cmd = new ChangeCardStatus(cardId, storymapId, newStatus, trackingOrder);

        const activityId = await uuid.create();

        const activity = {
            CardId: cardId,
            CardName: cardName,
            CardDescription: '',
            Datetime: new Date().toJSON(),
            OriginalContent: oldStatusName,
            StatusId: newStatus,
            StatusName: newStatusName,
            Id: activityId,
            StorymapId: storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.SubTaskStateChanged,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export class ChangeCardName extends Command<ICommandResult> {
    public BoardActivity: any;

    private constructor(public Id: string, public StorymapId: string, public Name: string, public CardType: string) {
        super('ChangeCardName');
    }

    public static async create(card: StorymapCard, newName: string) {
        const cmd = new ChangeCardName(card.id, card.storymapId, newName, getCardTypeName(card));

        const activityId = await uuid.create();

        const activity = {
            CardId: card.id,
            CardName: newName,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            EstimationUnit: '',
            OriginalContent: card.name,
            Id: activityId,
            StorymapId: card.storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: this.getActivityType(card),
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }

    private static getActivityType(card: StorymapCard) {
        switch (getCardType(card)) {
            case CardType.Activity:
                return BoardUserActivityType.ActivityNameChanged;
            case CardType.Task:
                return BoardUserActivityType.TaskNameChanged;
            case CardType.Subtask:
                return BoardUserActivityType.SubtaskNameChanged;
            case CardType.UnscheduledSubtask:
                return BoardUserActivityType.UnscheduledSubtaskNameChanged;
        }
    }
}

export interface Mention {
    MentionedUserId: string;
    Position: number;
    Context: string;
    HTMLContext?: string;
    MarkdownContext?: string;
}
/**
 * Result of ChangeCardDescription command with conflict detection support.
 */
export interface ChangeCardDescriptionResult extends ICommandResult {
    /** The description that was saved (on success). */
    SavedDescription?: string;
    /** True when a conflict was detected (description changed since user started editing). */
    IsConflict?: boolean;
    /** The current description in DB at time of conflict. */
    CurrentDescription?: string;
    /** The client baseline that triggered conflict (echoed back for UI). */
    OriginalDescription?: string;
    /** User ID of the last person who changed the description. */
    LastAuthorUserId?: string;
    /** Display name of the last person who changed the description. */
    LastAuthorName?: string;
    /** UTC timestamp of the last description change. */
    LastChangedAtUtc?: string;
}

export class ChangeCardDescription extends Command<ChangeCardDescriptionResult> {
    public BoardActivity: any;

    private constructor(
        public Id: string,
        public StorymapId: string,
        public Description: string,
        public NewMentions: Mention[],
        public CardType: string,
        /** The description the client believes it is editing from (baseline when user started editing). */
        public OriginalDescription: string
    ) {
        super('ChangeCardDescription');
    }

    public static async create(card: StorymapCard, newDescription: string, newMentions: EditorMention[] = [], baselineDescription?: string | null) {
        const cmdMentions: Mention[] = newMentions.map((mention) => ({
            MentionedUserId: mention.id,
            Position: mention.position,
            Context: mention.context,
            HTMLContext: mention.htmlContext,
            MarkdownContext: mention.markdownContext,
        }));

        // Use provided baseline or fall back to card's current description for conflict detection.
        // null = explicit "skip conflict detection" signal (sent as OriginalDescription=null to server).
        // undefined = fall back to card.description for backward compatibility with existing callers.
        const originalDescription = baselineDescription !== undefined ? baselineDescription : (card.description ?? '');

        const cmd = new ChangeCardDescription(card.id, card.storymapId, newDescription, cmdMentions, getCardTypeName(card), originalDescription);

        const activityId = await uuid.create();

        const activity = {
            CardId: card.id,
            CardName: card.name,
            Datetime: new Date().toJSON(),
            CardDescription: newDescription,
            EstimationUnit: '',
            OriginalContent: card.description,
            Id: activityId,
            StorymapId: card.storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: this.getActivityType(card),
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }

    private static getActivityType(card: StorymapCard) {
        switch (getCardType(card)) {
            case CardType.Activity:
                return BoardUserActivityType.ActivityDescriptionChanged;
            case CardType.Task:
                return BoardUserActivityType.TaskDescriptionChanged;
            case CardType.Subtask:
                return BoardUserActivityType.SubtaskDescriptionChanged;
            case CardType.UnscheduledSubtask:
                return BoardUserActivityType.UnscheduledSubtaskDescriptionChanged;
        }
    }
}

export class PushCardToIntegrationTool extends Command<PushCardToIntegrationToolResult> {
    public BoardActivity: any;

    private constructor(public CardId: string, public StorymapId: string) {
        super('PushCardToIntegrationTool');
    }

    public static async create(cardId: string, cardName: string, storymapId: string, externalToolName: string) {
        const cmd = new PushCardToIntegrationTool(cardId, storymapId);

        const activityId = await uuid.create();

        const activity = {
            CardId: cardId,
            CardName: cardName,
            Datetime: new Date().toJSON(),
            EstimationUnit: '',
            OriginalContent: '',
            Id: activityId,
            StorymapId: storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardPushedToIntegrationTool,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
            ExternalToolName: externalToolName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export interface PushCardToIntegrationToolResult {
    IsSuccess: boolean;
    Message: string;
    Message2: string;

    Activity: LinkedCard;
    Task: LinkedCard;
    Subtask: LinkedSubtask;
}

interface LinkedCard {
    LinkedItemUrl: string;
    LinkedItemId: string;
    LinkedItemKey: string;
    LinkedItemTypeId: string;
    LinkedItemTypeName: string;
}

interface LinkedSubtask extends LinkedCard {
    Status: number;
}

export class AddAnnotationToCard extends Command<ICommandResult> {
    public BoardActivity: any;

    private constructor(public CardId: string, public AnnotationId: string, public StorymapId: string) {
        super('AddAnnotationToCard');
    }

    public static async create(cardId: string, cardName: string, storymapId: string, annotationId: string, annotationName: string) {
        const cmd = new AddAnnotationToCard(cardId, annotationId, storymapId);

        const activityId = await uuid.create();

        const activity = {
            CardId: cardId,
            CardName: cardName,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            Content: annotationName,
            EstimationUnit: '',
            Id: activityId,
            StorymapId: storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardAnnotationsAdded,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

interface AddCardWebLinkResult extends ICommandResult {
    FaviconId: string;
    Name: string;
}

export class AddCardWebLink extends Command<AddCardWebLinkResult> {
    public BoardActivity: any;

    private constructor(public Id: string, public CardId: string, public Url: string, public Name: string, public StorymapId: string) {
        super('AddCardWebLink');
    }

    public static async create(id: string, card: StorymapCard, url: string) {
        const cmd = new AddCardWebLink(id, card.id, url, null, card.storymapId);

        const activityId = await uuid.create();

        const activity = {
            CardId: card.id,
            CardName: card.name,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            Content: url,
            EstimationUnit: '',
            Id: activityId,
            StorymapId: card.storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardWebLinkAdded,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export class AddCardAttachment extends Command<AddCardWebLinkResult> {
    public BoardActivity: any;

    private constructor(public Id: string, public FileInfoId: string, public StorymapId: string) {
        super('AddCardAttachment');
    }

    public static async create(id: string, card: StorymapCard, attachmentId: string, attachmentName: string) {
        const cmd = new AddCardAttachment(id, attachmentId, card.storymapId);

        const activityId = await uuid.create();

        const activity = {
            AttachmentId: attachmentId,
            AttachmentName: attachmentName,
            CardId: card.id,
            CardName: card.name,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            EstimationUnit: '',
            Id: activityId,
            StorymapId: card.storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardAttachmentAdded,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export class ChangeCardWebLinkName extends Command<ICommandResult> {
    public BoardActivity: any;

    private constructor(public Id: string, public Name: string, public StorymapId: string) {
        super('ChangeCardWebLinkName');
    }

    public static async create(id: string, card: StorymapCard, name: string) {
        const cmd = new ChangeCardWebLinkName(id, name, card.storymapId);

        const activityId = await uuid.create();

        const activity = {
            CardId: card.id,
            CardName: card.name,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            Content: name,
            EstimationUnit: '',
            Id: activityId,
            StorymapId: card.storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardWebLinkNameChanged,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

interface DeleteCardWebLinkResult extends ICommandResult {
    HasAttachment: boolean;
}

export class DeleteCardWebLink extends Command<DeleteCardWebLinkResult> {
    public BoardActivity: any;

    private constructor(public Id: string, public StorymapId: string) {
        super('DeleteCardWebLink');
    }

    public static async create(id: string, card: StorymapCard, url: string) {
        const cmd = new DeleteCardWebLink(id, card.storymapId);

        const activityId = await uuid.create();

        const activity = {
            CardId: card.id,
            CardName: card.name,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            Content: url,
            EstimationUnit: '',
            Id: activityId,
            StorymapId: card.storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardWebLinkDeleted,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

interface DeleteCardAttachmentResult extends ICommandResult {
    HasAttachment: boolean;
}

export class DeleteCardAttachment extends Command<DeleteCardAttachmentResult> {
    public BoardActivity: any;

    private constructor(public Id: string, public StorymapId: string) {
        super('DeleteCardAttachment');
    }

    public static async create(id: string, card: StorymapCard, attachmentId: string, attachmentName: string) {
        const cmd = new DeleteCardAttachment(id, card.storymapId);

        const activityId = await uuid.create();

        const activity = {
            AttachmentId: attachmentId,
            AttachmentName: attachmentName,
            CardId: card.id,
            CardName: card.name,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            EstimationUnit: '',
            Id: activityId,
            StorymapId: card.storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardAttachmentDeleted,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export class RemoveAnnotationFromCard extends Command<ICommandResult> {
    public BoardActivity: any;

    private constructor(public CardId: string, public AnnotationId: string, public StorymapId: string) {
        super('RemoveAnnotationFromCard');
    }

    public static async create(cardId: string, cardName: string, storymapId: string, annotationId: string, annotationName: string) {
        const cmd = new RemoveAnnotationFromCard(cardId, annotationId, storymapId);

        const activityId = await uuid.create();

        const activity = {
            CardId: cardId,
            CardName: cardName,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            Content: annotationName,
            EstimationUnit: '',
            Id: activityId,
            StorymapId: storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardAnnotationsRemoved,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export class SetAssigneeOnCard extends Command<ICommandResult> {
    public BoardActivity: any;

    private constructor(public CardId: string, public CollaboratorId: string, public StorymapId: string) {
        super('SetAssigneeOnCard');
    }

    public static async create(
        cardId: string,
        cardName: string,
        storymapId: string,
        collaborator?: StorymapCollaborator,
        oldCollaborator?: StorymapCollaborator
    ) {
        const cmd = new SetAssigneeOnCard(cardId, collaborator?.userId, storymapId);

        const activityId = await uuid.create();

        const activity = {
            CardId: cardId,
            CardName: cardName,
            Datetime: new Date().toJSON(),
            CardDescription: '',
            Content: collaborator?.name ?? 'Unassigned',
            OriginalContent: oldCollaborator?.name ?? 'Unassigned',
            EstimationUnit: '',
            Id: activityId,
            StorymapId: storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.AssigneeChanged,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export class ChangeCardEstimation extends Command<ICommandResult> {
    public BoardActivity: any;
    public CardType = 'Subtask';

    private constructor(public Id: string, public StorymapId: string, public Estimation: number) {
        super('ChangeCardEstimation');
    }

    public static async create(id: string, name: string, storymapId: string, estimation?: number) {
        const store = storymapStoreModule.useStorymapStore();
        const state = store.state;
        const card = state.subtasks.find((x) => x.id === id);

        const cmd = new ChangeCardEstimation(id, storymapId, estimation);

        const activityId = await uuid.create();

        const activity = {
            CardId: id,
            CardName: name,
            CardDescription: '',
            Datetime: new Date().toJSON(),
            OriginalContent: card.estimation,
            Estimation: estimation,
            EstimationUnit: state.estimationUnit,
            Id: activityId,
            StorymapId: storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.SubtaskEstimationChanged,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export class PasteCardFromClipboard extends Command<PasteCardFromClipboardResult> {
    public BoardActivity: any;

    private constructor(
        public DestinationCardId: string,
        public Index: number,
        public ParentActivityId: string,
        public ParentTaskId: string,
        public ReleaseId: string,
        public SourceCardId: string,
        public StorymapId: string
    ) {
        super('PasteCardFromClipboard');
    }

    public static async create(
        newCardId: string,
        sourceCardId: string,
        title: string,
        order: number,
        storymapId: string,
        activityId: string = null,
        taskId: string = null,
        releaseId: string = null
    ) {
        const cmd = new PasteCardFromClipboard(newCardId, order, activityId, taskId, releaseId, sourceCardId, storymapId);

        const boardActivityId = await uuid.create();

        const activity = {
            CardId: newCardId,
            CardName: title,
            Datetime: new Date().toJSON(),
            Id: boardActivityId,
            StorymapId: storymapId,
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.CardPastedFromClipboard,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;

        return cmd;
    }
}

export interface PasteCardFromClipboardResult extends CommandResult {
    CardKey: string;
}

export class UnscheduleSubtask extends Command<CommandResult> {
    public BoardActivity: any;

    constructor(public StorymapId: string, public Id: string) {
        super('UnscheduleSubtask');
    }

    public static create = async (id: string, storymapId: string, cardName: string, releaseId: string, releaseName: string) => {
        const boardActivityId = await uuid.create();
        const cmd = new UnscheduleSubtask(storymapId, id);

        const activity = {
            Id: boardActivityId,
            CardId: id,
            CardName: cardName,
            ReleaseId: releaseId,
            ReleaseName: releaseName,
            StorymapId: storymapId,
            Datetime: new Date().toJSON(),
            WorkspaceId: initAppData.currentWs.workspaceId,
            Type: BoardUserActivityType.SubtaskUnscheduled,
            UserId: initAppData.currentUser.currentUserShortId,
            Username: initAppData.currentUser.currentUserFullName,
        };

        cmd.BoardActivity = activity;
        return cmd;
    };
}

export class UnlinkFeedbackFromStorymapCard extends Command<CommandResult> {
    constructor(public StorymapId: string, public StorymapCardId: string, public FeedbackId: string) {
        super('UnlinkFeedbackFromStorymapCard');
    }
}

export class SetCardIsWatched extends Command<ICommandResult> {
    private constructor(public CardId: string, public StorymapId: string, public IsWatched: boolean) {
        super('SetCardIsWatched');
    }

    public static async create(cardId: string, storymapId: string, isWatched: boolean) {
        const cmd = new SetCardIsWatched(cardId, storymapId, isWatched);
        return cmd;
    }
}
